import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart, ArrowRight, ArrowLeft, Truck, ShieldCheck, Clock, Zap,
  TrendingUp, Grid, Package, Cpu, Shirt, Home as HomeIcon, Heart, Tag
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useCart } from '../../context/CartContext';
import { baseURL } from '../../utils/api';
import { useCachedFetch } from '../../utils/cache';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './Home.css';

import SliderTech from '../../assets/images/slider_tech_1776418800000_1776420002051.png';
import SliderLifestyle from '../../assets/images/slider_lifestyle_1776418800000_1776420028214.png';
import SliderLuxury from '../../assets/images/slider_luxury_1776418800000_1776420056270.png';

const resolveImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) return img;
  return `${baseURL}${img}`;
};

const pickCategoryIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('tech') || n.includes('electron') || n.includes('gadget') || n.includes('phone') || n.includes('computer')) return Cpu;
  if (n.includes('fashion') || n.includes('cloth') || n.includes('apparel') || n.includes('wear')) return Shirt;
  if (n.includes('home') || n.includes('living') || n.includes('kitchen') || n.includes('furni')) return HomeIcon;
  if (n.includes('health') || n.includes('beauty') || n.includes('wellness') || n.includes('care')) return Heart;
  return Tag;
};

const Home = () => {
  const { addToCart } = useCart();
  const { data: productsData, loading } = useCachedFetch('/api/products', { ttl: 10 * 60 * 1000 });
  const products = useMemo(() => (Array.isArray(productsData) ? productsData : []), [productsData]);

  const heroPrevRef = useRef(null);
  const heroNextRef = useRef(null);
  const carouselPrevRef = useRef(null);
  const carouselNextRef = useRef(null);

  const featuredProducts = useMemo(() => products.slice(0, 8), [products]);

  const categories = useMemo(() => {
    const counts = products.reduce((acc, p) => {
      const cat = p.category || 'General';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([name, count]) => ({
        name,
        count,
        Icon: pickCategoryIcon(name),
      }));
  }, [products]);

  return (
    <div className="ec-home-root">
      {/* Hero Slider */}
      <section className="ec-hero">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          navigation={{ prevEl: heroPrevRef.current, nextEl: heroNextRef.current }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = heroPrevRef.current;
            swiper.params.navigation.nextEl = heroNextRef.current;
          }}
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
          loop={true}
          className="ec-main-swiper"
        >
          <button ref={heroPrevRef} type="button" className="ec-swiper-nav ec-swiper-nav-prev ec-swiper-nav-hero" aria-label="Previous slide">
            <ArrowLeft size={20} strokeWidth={2.25} />
          </button>
          <button ref={heroNextRef} type="button" className="ec-swiper-nav ec-swiper-nav-next ec-swiper-nav-hero" aria-label="Next slide">
            <ArrowRight size={20} strokeWidth={2.25} />
          </button>
          <SwiperSlide>
            <div className="ec-slide">
              <img src={SliderTech} alt="Tech" className="ec-slide-bg" />
              <div className="ec-slide-content">
                <span className="ec-badge">New Arrival</span>
                <h1>Next-Gen <br />Innovation</h1>
                <p>Experience the future of personal technology today.</p>
                <Link to="/shop" className="ec-glass-btn">Explore Now <ArrowRight size={18} /></Link>
              </div>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="ec-slide">
              <img src={SliderLifestyle} alt="Lifestyle" className="ec-slide-bg" />
              <div className="ec-slide-content">
                <span className="ec-badge">Essential Collection</span>
                <h1>Elevated <br />Living</h1>
                <p>Minimalist essentials for your modern lifestyle.</p>
                <Link to="/shop" className="ec-glass-btn">Shop Collection <ArrowRight size={18} /></Link>
              </div>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="ec-slide">
              <img src={SliderLuxury} alt="Luxury" className="ec-slide-bg" />
              <div className="ec-slide-content">
                <span className="ec-badge">Premium Select</span>
                <h1>Timeless <br />Elegance</h1>
                <p>Hand-picked luxury items for the discerning eye.</p>
                <Link to="/shop" className="ec-glass-btn">Discover More <ArrowRight size={18} /></Link>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </section>

      {/* Features Bar */}
      <section className="ec-features">
        <div className="ec-features-grid">
          <div className="ec-feature-item">
            <Truck className="ec-feature-icon" />
            <div className="ec-feature-text">
              <h4>Free Shipping</h4>
              <p>On all orders over Rs. 5000</p>
            </div>
          </div>
          <div className="ec-feature-item">
            <ShieldCheck className="ec-feature-icon" />
            <div className="ec-feature-text">
              <h4>Secure Payments</h4>
              <p>Encrypted transactions</p>
            </div>
          </div>
          <div className="ec-feature-item">
            <Clock className="ec-feature-icon" />
            <div className="ec-feature-text">
              <h4>24/7 Support</h4>
              <p>Dedicated customer care</p>
            </div>
          </div>
          <div className="ec-feature-item">
            <Zap className="ec-feature-icon" />
            <div className="ec-feature-text">
              <h4>Quick Delivery</h4>
              <p>Same day dispatch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories */}
      <section className="ec-home-section">
        <div className="ec-container">
          <div className="ec-section-header-center">
            <span className="ec-subtitle">Browse Our Collection</span>
            <h2 className="ec-title">Popular Categories</h2>
          </div>

          {loading ? (
            <div className="ec-loading-spinner">
              <div className="spinner" />
              <p>Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="ec-empty-block">
              <Package size={32} />
              <h3>No categories yet</h3>
              <p>Categories will appear here once products are added.</p>
            </div>
          ) : (
            <div className="ec-cat-grid-large">
              {categories.map(({ name, count, Icon }) => (
                <Link
                  to={`/shop?category=${encodeURIComponent(name)}`}
                  key={name}
                  className="ec-cat-card-premium"
                >
                  <div className="ec-cat-visual">
                    <Icon size={36} strokeWidth={1.75} />
                  </div>
                  <div className="ec-cat-info">
                    <h3>{name}</h3>
                    <span>{count} {count === 1 ? 'Product' : 'Products'}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured / Trending Products */}
      <section className="ec-home-section ec-featured-bg">
        <div className="ec-container">
          <div className="ec-section-header">
            <div>
              <span className="ec-subtitle">Our Favorites</span>
              <h2 className="ec-title">Trending Products</h2>
            </div>
            <Link to="/shop" className="ec-link-btn">
              View All Products <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="ec-loading-spinner">
              <div className="spinner" />
              <p>Curating trending items...</p>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="ec-empty-block">
              <Package size={32} />
              <h3>No products available yet</h3>
              <p>Check back soon — new products are on their way.</p>
            </div>
          ) : (
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              navigation={{ prevEl: carouselPrevRef.current, nextEl: carouselNextRef.current }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = carouselPrevRef.current;
                swiper.params.navigation.nextEl = carouselNextRef.current;
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop={featuredProducts.length > 4}
              slidesPerGroup={1}
              spaceBetween={24}
              slidesPerView={1.2}
              breakpoints={{
                480: { slidesPerView: 2, spaceBetween: 16, slidesPerGroup: 1 },
                768: { slidesPerView: 3, spaceBetween: 20, slidesPerGroup: 1 },
                1024: { slidesPerView: 4, spaceBetween: 24, slidesPerGroup: 1 },
              }}
              className="ec-product-carousel"
            >
              <button ref={carouselPrevRef} type="button" className="ec-swiper-nav ec-swiper-nav-prev" aria-label="Previous products">
                <ArrowLeft size={18} strokeWidth={2.25} />
              </button>
              <button ref={carouselNextRef} type="button" className="ec-swiper-nav ec-swiper-nav-next" aria-label="Next products">
                <ArrowRight size={18} strokeWidth={2.25} />
              </button>
              {featuredProducts.map((product) => {
                const imgSrc = resolveImage(product.image);
                return (
                  <SwiperSlide key={product.id}>
                    <div className="ec-product-card-glass">
                      <div className="ec-product-img-wrap">
                        {imgSrc ? (
                          <img src={imgSrc} alt={product.name} />
                        ) : (
                          <div className="ec-product-img-fallback">
                            <Package size={36} />
                          </div>
                        )}
                        <Link to={`/product/${product.slug}`} className="ec-quick-view">Quick View</Link>
                      </div>
                      <div className="ec-product-details">
                        <span className="ec-p-cat">{product.category}</span>
                        <h3 className="ec-p-name">{product.name}</h3>
                        <div className="ec-p-bottom">
                          <span className="ec-p-price">Rs. {Number(product.price).toLocaleString()}</span>
                          <button
                            className="ec-add-cart-btn"
                            onClick={() => addToCart(product)}
                            aria-label="Add to cart"
                          >
                            <ShoppingCart size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
        </div>
      </section>

      {/* Premium Benefits - New Beautiful Section */}
      <section className="ec-home-section ec-benefits-section">
        <div className="ec-container">
          <div className="ec-section-header-center">
            <span className="ec-subtitle">The Alpha Advantage</span>
            <h2 className="ec-title">Why Shop With Us?</h2>
          </div>
          
          <div className="ec-benefits-grid">
            <div className="ec-benefit-card glass">
              <div className="ec-benefit-icon"><ShieldCheck size={32} /></div>
              <h3>Curated Quality</h3>
              <p>Every product is hand-selected and verified for absolute quality and authenticity.</p>
            </div>
            <div className="ec-benefit-card glass">
              <div className="ec-benefit-icon"><Zap size={32} /></div>
              <h3>Instant Rewards</h3>
              <p>Earn exclusive Alpha points on every purchase and unlock premium dashboard perks.</p>
            </div>
            <div className="ec-benefit-card glass">
              <div className="ec-benefit-icon"><ShoppingCart size={32} /></div>
              <h3>Seamless Experience</h3>
              <p>From one-click checkout to real-time tracking, we prioritize your convenience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className="ec-values">
        <div className="ec-container">
          <div className="ec-values-grid">
            <div className="ec-value-card">
              <TrendingUp size={32} />
              <h3>Market Leader</h3>
              <p>Voted #1 for customer satisfaction and product quality in 2025.</p>
            </div>
            <div className="ec-value-card">
              <Grid size={32} />
              <h3>Diverse Palette</h3>
              <p>Over 10,000 unique SKU's curated from the best international brands.</p>
            </div>
            <div className="ec-value-card">
              <ShieldCheck size={32} />
              <h3>Authenticity Guaranteed</h3>
              <p>Every single product is verified by our team of experts.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
